import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { FileText, Send, Clock, CheckCircle, XCircle, Download, Plus, Upload, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'En attente', class: 'bg-amber-100 text-amber-700', icon: Clock },
  completed: { label: 'Complete', class: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { label: 'Rejete', class: 'bg-red-100 text-red-700', icon: XCircle },
};

const requestTypeLabels = {
  devis: 'Demande de Devis',
  facture: 'Demande de Facture',
  contrat: 'Demande de Contrat',
};

export const DocuClient = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formType, setFormType] = useState('devis');
  const [submitting, setSubmitting] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [downloading, setDownloading] = useState(null);

  // Form state
  const [form, setForm] = useState({
    raison_sociale: '',
    nom_commercial: '',
    emails: '',
    siret: '',
    tva_intra: '',
    adresse_facturation: '',
    adresse_livraison: '',
    iban: '',
    bic: '',
    titulaire: '',
    devise: 'EUR',
    langue: 'FR',
    moyen_paiement: '',
    montant: '',
    duree_annees: '',
    duree_mois: '',
    echelonnement: '',
    remise_commerciale: '',
    commentaires: '',
  });
  const [devisSigne, setDevisSigne] = useState(null);
  const [contratSigne, setContratSigne] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await fetch(`${API}/docu-client`, { credentials: 'include' });
      if (response.ok) {
        setRequests(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      raison_sociale: '',
      nom_commercial: '',
      emails: '',
      siret: '',
      tva_intra: '',
      adresse_facturation: '',
      adresse_livraison: '',
      iban: '',
      bic: '',
      titulaire: '',
      devise: 'EUR',
      langue: 'FR',
      moyen_paiement: '',
      montant: '',
      duree_annees: '',
      duree_mois: '',
      echelonnement: '',
      remise_commerciale: '',
      commentaires: '',
    });
    setDevisSigne(null);
    setContratSigne(null);
  };

  const openForm = (type) => {
    setFormType(type);
    resetForm();
    setIsFormOpen(true);
  };

  const handleSubmit = async () => {
    // Validation pour Devis - tous les champs obligatoires
    if (formType === 'devis') {
      const requiredFields = [
        { field: 'raison_sociale', label: 'Raison sociale' },
        { field: 'emails', label: 'Adresse e-mail' },
        { field: 'siret', label: 'SIRET' },
        { field: 'tva_intra', label: 'N° TVA intracommunautaire' },
        { field: 'adresse_facturation', label: 'Adresse de facturation' },
        { field: 'iban', label: 'IBAN' },
        { field: 'bic', label: 'BIC' },
        { field: 'titulaire', label: 'Titulaire du compte' },
        { field: 'moyen_paiement', label: 'Moyen de paiement' },
        { field: 'echelonnement', label: 'Echelonnage du paiement' },
      ];
      for (const { field, label } of requiredFields) {
        if (!form[field]) {
          toast.error(`Le champ "${label}" est obligatoire`);
          return;
        }
      }
      const montantNum = parseFloat(form.montant);
      if (isNaN(montantNum) || montantNum <= 0) {
        toast.error('Le montant est obligatoire et doit etre superieur a 0');
        return;
      }
      const annees = parseInt(form.duree_annees) || 0;
      const mois = parseInt(form.duree_mois) || 0;
      if (annees <= 0 && mois <= 0) {
        toast.error('Renseignez la duree de la collaboration (annees ou mois)');
        return;
      }
      if (mois < 0 || mois > 11) {
        toast.error('Le nombre de mois doit etre compris entre 0 et 11');
        return;
      }
    }
    
    // Validation pour Facture - memes champs que devis + fichiers
    if (formType === 'facture') {
      const requiredFields = [
        { field: 'raison_sociale', label: 'Raison sociale' },
        { field: 'emails', label: 'Adresse e-mail' },
        { field: 'siret', label: 'SIRET' },
        { field: 'tva_intra', label: 'N° TVA intracommunautaire' },
        { field: 'adresse_facturation', label: 'Adresse de facturation' },
        { field: 'iban', label: 'IBAN' },
        { field: 'bic', label: 'BIC' },
        { field: 'titulaire', label: 'Titulaire du compte' },
        { field: 'moyen_paiement', label: 'Moyen de paiement' },
        { field: 'echelonnement', label: 'Echelonnage du paiement' },
      ];
      for (const { field, label } of requiredFields) {
        if (!form[field]) {
          toast.error(`Le champ "${label}" est obligatoire`);
          return;
        }
      }
      const montantNum = parseFloat(form.montant);
      if (isNaN(montantNum) || montantNum <= 0) {
        toast.error('Le montant est obligatoire et doit etre superieur a 0');
        return;
      }
      const annees = parseInt(form.duree_annees) || 0;
      const mois = parseInt(form.duree_mois) || 0;
      if (annees <= 0 && mois <= 0) {
        toast.error('Renseignez la duree de la collaboration (annees ou mois)');
        return;
      }
      if (mois < 0 || mois > 11) {
        toast.error('Le nombre de mois doit etre compris entre 0 et 11');
        return;
      }
      if (!devisSigne || !contratSigne) {
        toast.error('Le devis signe et le contrat signe sont obligatoires');
        return;
      }
    }
    
    // Validation pour Contrat - juste les fichiers obligatoires
    if (formType === 'contrat' && (!devisSigne || !contratSigne)) {
      toast.error('Le devis signe et le contrat signe sont obligatoires');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('request_type', formType);
      
      Object.entries(form).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });
      
      if (devisSigne) formData.append('devis_signe', devisSigne);
      if (contratSigne) formData.append('contrat_signe', contratSigne);

      const response = await fetch(`${API}/docu-client`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        toast.success('Demande envoyee avec succes');
        setIsFormOpen(false);
        fetchRequests();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadFile = async (requestId, fileType, filename) => {
    setDownloading(`${requestId}-${fileType}`);
    try {
      const response = await fetch(`${API}/docu-client/${requestId}/download/${fileType}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || `${fileType}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Fichier telecharge');
      } else {
        toast.error('Erreur de telechargement');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur de telechargement');
    } finally {
      setDownloading(null);
    }
  };

  const openDetail = (req) => {
    setSelectedRequest(req);
    setIsDetailOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-[Manrope]" data-testid="docu-client-title">
            Docu-Client
          </h1>
          <p className="text-slate-500 mt-1">Demandez vos documents clients (devis, factures, contrats)</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card 
          className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => openForm('devis')}
          data-testid="new-devis-card"
        >
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-4 rounded-xl bg-blue-100">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-lg">Demande de Devis</p>
              <p className="text-sm text-slate-500">Obtenir un devis client</p>
            </div>
            <Plus className="w-5 h-5 text-slate-400 ml-auto" />
          </CardContent>
        </Card>

        <Card 
          className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => openForm('facture')}
          data-testid="new-facture-card"
        >
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-4 rounded-xl bg-emerald-100">
              <FileText className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="font-semibold text-lg">Demande de Facture</p>
              <p className="text-sm text-slate-500">Avec devis et contrat signes</p>
            </div>
            <Plus className="w-5 h-5 text-slate-400 ml-auto" />
          </CardContent>
        </Card>

        <Card 
          className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => openForm('contrat')}
          data-testid="new-contrat-card"
        >
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-4 rounded-xl bg-purple-100">
              <FileText className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="font-semibold text-lg">Demande de Contrat</p>
              <p className="text-sm text-slate-500">Avec documents signes</p>
            </div>
            <Plus className="w-5 h-5 text-slate-400 ml-auto" />
          </CardContent>
        </Card>
      </div>

      {/* Requests History */}
      <Card className="border-0 shadow-sm" data-testid="requests-table">
        <CardHeader>
          <CardTitle>Mes demandes</CardTitle>
          <CardDescription>Historique de vos demandes de documents</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Type</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    Aucune demande
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((req) => {
                  const StatusIcon = statusConfig[req.status]?.icon || Clock;
                  return (
                    <TableRow key={req.request_id} className="hover:bg-slate-50">
                      <TableCell className="font-medium">
                        {requestTypeLabels[req.request_type] || req.request_type}
                      </TableCell>
                      <TableCell>{req.raison_sociale || '-'}</TableCell>
                      <TableCell className="text-slate-500">
                        {new Date(req.created_at).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusConfig[req.status]?.class} border`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig[req.status]?.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openDetail(req)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {req.status === 'completed' && req.response_filename && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => downloadFile(req.request_id, 'response', req.response_filename)}
                              disabled={downloading === `${req.request_id}-response`}
                            >
                              {downloading === `${req.request_id}-response` ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Download className="w-4 h-4 mr-1" />
                                  Telecharger
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{requestTypeLabels[formType]}</DialogTitle>
            <DialogDescription>
              {formType === 'devis' && 'Remplissez les informations du client pour obtenir un devis'}
              {formType === 'facture' && 'Televersez les documents signes pour obtenir la facture'}
              {formType === 'contrat' && 'Televersez les documents signes pour finaliser le contrat'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Pour Contrat: seulement les fichiers + commentaire */}
            {formType === 'contrat' ? (
              <>
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm text-slate-700 border-b pb-2">Documents a telecharger (obligatoires)</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Devis signe *</Label>
                      <div className="border-2 border-dashed rounded-lg p-4 text-center">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => setDevisSigne(e.target.files[0])}
                          className="hidden"
                          id="devis-upload-contrat"
                        />
                        <label htmlFor="devis-upload-contrat" className="cursor-pointer">
                          <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                          <p className="text-sm text-slate-500">
                            {devisSigne ? devisSigne.name : 'Cliquez pour telecharger'}
                          </p>
                        </label>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Contrat signe par l&apos;etablissement *</Label>
                      <div className="border-2 border-dashed rounded-lg p-4 text-center">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => setContratSigne(e.target.files[0])}
                          className="hidden"
                          id="contrat-upload-contrat"
                        />
                        <label htmlFor="contrat-upload-contrat" className="cursor-pointer">
                          <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                          <p className="text-sm text-slate-500">
                            {contratSigne ? contratSigne.name : 'Cliquez pour telecharger'}
                          </p>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Commentaires (facultatif)</Label>
                  <Textarea
                    value={form.commentaires}
                    onChange={(e) => setForm({ ...form, commentaires: e.target.value })}
                    placeholder="Informations supplementaires..."
                    rows={3}
                  />
                </div>
              </>
            ) : (
              <>
            {/* Client Info Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-slate-700 border-b pb-2">Informations Client</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Raison sociale *</Label>
                  <Input
                    value={form.raison_sociale}
                    onChange={(e) => setForm({ ...form, raison_sociale: e.target.value })}
                    placeholder="Nom de l'etablissement"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nom commercial</Label>
                  <Input
                    value={form.nom_commercial}
                    onChange={(e) => setForm({ ...form, nom_commercial: e.target.value })}
                    placeholder="Nom commercial"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Adresses e-mails * (separees par virgules)</Label>
                  <Input
                    value={form.emails}
                    onChange={(e) => setForm({ ...form, emails: e.target.value })}
                    placeholder="email1@example.com, email2@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>SIRET *</Label>
                  <Input
                    value={form.siret}
                    onChange={(e) => setForm({ ...form, siret: e.target.value })}
                    placeholder="12345678901234"
                  />
                </div>
                <div className="space-y-2">
                  <Label>N° TVA intracommunautaire *</Label>
                  <Input
                    value={form.tva_intra}
                    onChange={(e) => setForm({ ...form, tva_intra: e.target.value })}
                    placeholder="FR12345678901"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Adresse de facturation *</Label>
                  <Textarea
                    value={form.adresse_facturation}
                    onChange={(e) => setForm({ ...form, adresse_facturation: e.target.value })}
                    placeholder="Adresse complete"
                    rows={2}
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Adresse de livraison (si differente)</Label>
                  <Textarea
                    value={form.adresse_livraison}
                    onChange={(e) => setForm({ ...form, adresse_livraison: e.target.value })}
                    placeholder="Adresse de livraison"
                    rows={2}
                  />
                </div>
              </div>
            </div>

            {/* Bank Info Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-slate-700 border-b pb-2">Coordonnees bancaires</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>IBAN *</Label>
                  <Input
                    value={form.iban}
                    onChange={(e) => setForm({ ...form, iban: e.target.value })}
                    placeholder="FR76..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>BIC *</Label>
                  <Input
                    value={form.bic}
                    onChange={(e) => setForm({ ...form, bic: e.target.value })}
                    placeholder="BNPAFRPP"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Titulaire du compte *</Label>
                  <Input
                    value={form.titulaire}
                    onChange={(e) => setForm({ ...form, titulaire: e.target.value })}
                    placeholder="Nom du titulaire"
                  />
                </div>
              </div>
            </div>

            {/* Preferences Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-slate-700 border-b pb-2">Preferences</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Devise *</Label>
                  <Select value={form.devise} onValueChange={(v) => setForm({ ...form, devise: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Langue *</Label>
                  <Select value={form.langue} onValueChange={(v) => setForm({ ...form, langue: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FR">Francais</SelectItem>
                      <SelectItem value="EN">Anglais</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Moyen de paiement *</Label>
                  <Select value={form.moyen_paiement} onValueChange={(v) => setForm({ ...form, moyen_paiement: v })}>
                    <SelectTrigger><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="virement">Virement</SelectItem>
                      <SelectItem value="prelevement">Prelevement</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="cb">Carte bancaire</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Offer Section */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm text-slate-700 border-b pb-2">Montant et duree</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Montant total TTC (EUR) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    inputMode="decimal"
                    value={form.montant}
                    onChange={(e) => setForm({ ...form, montant: e.target.value })}
                    placeholder="Ex: 1234.56"
                    data-testid="docu-montant"
                  />
                  <p className="text-xs text-slate-500">Chiffres avec centimes obligatoires (ex: 1234.56)</p>
                </div>
                <div className="space-y-2">
                  <Label>Duree de la collaboration *</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Input
                        type="number"
                        min="0"
                        value={form.duree_annees}
                        onChange={(e) => setForm({ ...form, duree_annees: e.target.value })}
                        placeholder="Annees"
                        data-testid="docu-duree-annees"
                      />
                      <p className="text-xs text-slate-500 mt-1 text-center">Annees</p>
                    </div>
                    <div>
                      <Input
                        type="number"
                        min="0"
                        max="11"
                        value={form.duree_mois}
                        onChange={(e) => setForm({ ...form, duree_mois: e.target.value })}
                        placeholder="Mois"
                        data-testid="docu-duree-mois"
                      />
                      <p className="text-xs text-slate-500 mt-1 text-center">Mois (0-11)</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Echelonnage du paiement *</Label>
                  <Select value={form.echelonnement} onValueChange={(v) => setForm({ ...form, echelonnement: v })}>
                    <SelectTrigger><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="comptant">Comptant</SelectItem>
                      <SelectItem value="3_echeances">3 echeances</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Remise commerciale (%)</Label>
                  <Input
                    type="number"
                    value={form.remise_commerciale}
                    onChange={(e) => setForm({ ...form, remise_commerciale: e.target.value })}
                    placeholder="0"
                    min="0"
                    max="100"
                  />
                </div>
              </div>
            </div>

            {/* Comments */}
            <div className="space-y-2">
              <Label>Commentaires</Label>
              <Textarea
                value={form.commentaires}
                onChange={(e) => setForm({ ...form, commentaires: e.target.value })}
                placeholder="Informations supplementaires..."
                rows={3}
              />
            </div>

            {/* File Uploads (for facture only - contrat has its own section above) */}
            {formType === 'facture' && (
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-slate-700 border-b pb-2">Documents a telecharger (obligatoires)</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Devis signe *</Label>
                    <div className="border-2 border-dashed rounded-lg p-4 text-center">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => setDevisSigne(e.target.files[0])}
                        className="hidden"
                        id="devis-upload"
                      />
                      <label htmlFor="devis-upload" className="cursor-pointer">
                        <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                        <p className="text-sm text-slate-500">
                          {devisSigne ? devisSigne.name : 'Cliquez pour telecharger'}
                        </p>
                      </label>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Contrat signe *</Label>
                    <div className="border-2 border-dashed rounded-lg p-4 text-center">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => setContratSigne(e.target.files[0])}
                        className="hidden"
                        id="contrat-upload"
                      />
                      <label htmlFor="contrat-upload" className="cursor-pointer">
                        <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                        <p className="text-sm text-slate-500">
                          {contratSigne ? contratSigne.name : 'Cliquez pour telecharger'}
                        </p>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFormOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Envoyer la demande
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Details de la demande</DialogTitle>
            <DialogDescription>
              {selectedRequest && requestTypeLabels[selectedRequest.request_type]}
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Raison sociale</p>
                  <p className="font-medium">{selectedRequest.raison_sociale || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Montant</p>
                  <p className="font-medium">{selectedRequest.montant != null ? `${Number(selectedRequest.montant).toFixed(2)} EUR` : '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Duree</p>
                  <p className="font-medium">
                    {(selectedRequest.duree_annees || selectedRequest.duree_mois)
                      ? `${selectedRequest.duree_annees || 0} an(s) ${selectedRequest.duree_mois || 0} mois`
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Statut</p>
                  <Badge className={`${statusConfig[selectedRequest.status]?.class} mt-1`}>
                    {statusConfig[selectedRequest.status]?.label}
                  </Badge>
                </div>
                <div>
                  <p className="text-slate-500">Date</p>
                  <p className="font-medium">{new Date(selectedRequest.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
              </div>

              {selectedRequest.response_comment && (
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-sm font-medium text-emerald-800">Commentaire admin:</p>
                  <p className="text-sm text-emerald-700">{selectedRequest.response_comment}</p>
                </div>
              )}

              {selectedRequest.rejection_reason && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm font-medium text-red-800">Motif du rejet:</p>
                  <p className="text-sm text-red-700 whitespace-pre-wrap">{selectedRequest.rejection_reason}</p>
                </div>
              )}

              {/* Documents uploaded by the user (devis / contrat signes) */}
              {(selectedRequest.devis_signe_filename || selectedRequest.contrat_signe_filename) && (
                <div className="border-t pt-4 space-y-2">
                  <h4 className="font-semibold text-sm">Vos documents joints</h4>
                  {selectedRequest.devis_signe_filename && (
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => downloadFile(selectedRequest.request_id, 'devis_signe', selectedRequest.devis_signe_filename)}
                      disabled={downloading === `${selectedRequest.request_id}-devis_signe`}
                      data-testid="dl-user-devis"
                    >
                      {downloading === `${selectedRequest.request_id}-devis_signe` ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Devis signe : {selectedRequest.devis_signe_filename}
                    </Button>
                  )}
                  {selectedRequest.contrat_signe_filename && (
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => downloadFile(selectedRequest.request_id, 'contrat_signe', selectedRequest.contrat_signe_filename)}
                      disabled={downloading === `${selectedRequest.request_id}-contrat_signe`}
                      data-testid="dl-user-contrat"
                    >
                      {downloading === `${selectedRequest.request_id}-contrat_signe` ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Contrat signe : {selectedRequest.contrat_signe_filename}
                    </Button>
                  )}
                </div>
              )}

              {selectedRequest.status === 'completed' && selectedRequest.response_filename && (
                <Button 
                  className="w-full"
                  onClick={() => downloadFile(selectedRequest.request_id, 'response', selectedRequest.response_filename)}
                  disabled={downloading === `${selectedRequest.request_id}-response`}
                >
                  {downloading === `${selectedRequest.request_id}-response` ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Telecharger le document
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
